/**
 * priorityEngine.js
 *
 * Computes a ranked priority report from ministry state + pastoral insights.
 * Pure functions — no side effects, no external deps.
 *
 * Outputs:
 *   pressure:        "low" | "medium" | "high" | "critical"
 *   pastoralLoad:    0.0 – 1.0
 *   items:           PriorityItem[] (ranked by urgency)
 *   topRecommendation: string|null
 */

/**
 * @typedef {{ type: string, urgency: number, label: string, action: string }} PriorityItem
 */

function computeMinistryPressure(ministryState, insights) {
  const highCount = insights.filter((i) => i.severity === "high").length;
  const { pendingVisits = 0, followupPeople = 0 } = ministryState;

  if (highCount >= 3 || (pendingVisits >= 12 && followupPeople >= 6)) return "critical";
  if (highCount >= 2 || (pendingVisits >= 8  && followupPeople >= 4)) return "high";
  if (highCount >= 1 || pendingVisits >= 4  || followupPeople >= 2)   return "medium";
  return "low";
}

function computePastoralLoad(ministryState) {
  const { todayVisits = 0, pendingVisits = 0, upcomingEvents = 0, activePrayers = 0 } = ministryState;
  const raw = (todayVisits * 0.30) + (pendingVisits * 0.10) + (upcomingEvents * 0.05) + (activePrayers * 0.05);
  return Math.min(1.0, raw / 5.0);
}

/**
 * Build a priority report from ministry state and pastoral insights.
 *
 * @param {Object} ministryState
 * @param {import("./pastoralReasoningEngine").PastoralInsight[]} insights
 * @returns {{ pressure: string, pastoralLoad: number, items: PriorityItem[], topRecommendation: string|null }}
 */
export function buildPriorityReport(ministryState, insights) {
  const pressure          = computeMinistryPressure(ministryState, insights);
  const pastoralLoad      = computePastoralLoad(ministryState);
  const topRecommendation = insights[0]?.suggestion ?? null;

  const items = insights.map((insight, i) => ({
    type:    insight.type,
    urgency: i === 0 ? 1.0 : i === 1 ? 0.75 : 0.50,
    label:   insight.insight,
    action:  insight.suggestion,
  }));

  return { pressure, pastoralLoad, items, topRecommendation };
}
